import { DATE_PRESETS, DatePreset } from "../reports.utils";

/**
 * "Save as my report" (owner 2026-10-26): a standard report saved with its filters and
 * choices. Stored in api/custom-reports like the AI-made reports (`spec` = this envelope;
 * the server checks it in StandardReportView.cs). Dates stay relative ("Last month").
 */

export const STANDARD_REPORTS: Record<
  string,
  { name: string; billing?: boolean }
> = {
  workload: { name: "Team workload" },
  deadlines: { name: "Deadlines" },
  completed: { name: "Completed work" },
  "time-in-status": { name: "Time in status" },
  waiting: { name: "Waiting on client" },
  budget: { name: "Budget vs actual hours" },
  time: { name: "Time report" },
  unbilled: { name: "Unbilled time (WIP)", billing: true },
  aging: { name: "Unpaid invoices by age", billing: true },
  revenue: { name: "Revenue", billing: true },
  // Added 2026-11-05 (nine new reports; the server's StandardReportView.Reports lists them too).
  forecast: { name: "Busy season forecast" },
  throughput: { name: "Work started vs. finished" },
  utilization: { name: "Team utilization" },
  profitability: { name: "Client profitability", billing: true },
  payments: { name: "Payments received", billing: true },
  "client-requests": { name: "Client requests" },
  "by-owner": { name: "Clients by owner / manager" },
  "quiet-clients": { name: "Quiet clients" },
  portal: { name: "Client portal and signatures" },
};

export type ViewFilters = {
  preset: DatePreset | "";
  userIds: string[];
  clientIds: string[];
  workTypeIds: string[];
};

export type StandardView = {
  kind: "standard";
  version: 1;
  report: string;
  filters: ViewFilters;
  extras: Record<string, string>;
  labels: string[];
};

const PRESETS = DATE_PRESETS.map((p) => p.value);

const ids = (raw: unknown) =>
  (Array.isArray(raw) ? raw : [])
    .map((x) => String(x ?? "").trim())
    .filter((x, i, all) => x && x.length <= 64 && all.indexOf(x) === i)
    .slice(0, 200);

/** Cleans a saved standard view (null when it isn't one we can open). */
export const normalizeStandardView = (raw: unknown): StandardView | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (
    r.kind !== "standard" ||
    typeof r.report !== "string" ||
    !STANDARD_REPORTS[r.report]
  )
    return null;
  const f = (r.filters || {}) as Record<string, unknown>;
  const extras: Record<string, string> = {};
  Object.entries((r.extras || {}) as Record<string, unknown>)
    .slice(0, 10)
    .forEach(([k, v]) => {
      if (/^[A-Za-z0-9_-]{1,40}$/.test(k) && typeof v === "string")
        extras[k] = v.slice(0, 80);
    });
  return {
    kind: "standard",
    version: 1,
    report: r.report,
    filters: {
      preset: PRESETS.includes(f.preset as DatePreset)
        ? (f.preset as DatePreset)
        : "",
      userIds: ids(f.userIds),
      clientIds: ids(f.clientIds),
      workTypeIds: ids(f.workTypeIds),
    },
    extras,
    labels: (Array.isArray(r.labels) ? r.labels : [])
      .map((l) =>
        String(l ?? "")
          .trim()
          .slice(0, 60),
      )
      .filter(Boolean)
      .slice(0, 10),
  };
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** The chips shown on the card and in the Save window ("Last month", "2 team members", "By client"). */
export const viewLabels = (
  filters: ViewFilters,
  extraLabels: string[],
  hasDate: boolean,
): string[] => {
  const out: string[] = [];
  if (hasDate && filters.preset)
    out.push(DATE_PRESETS.find((p) => p.value === filters.preset)?.label || "");
  if (filters.userIds.length)
    out.push(plural(filters.userIds.length, "team member", "team members"));
  if (filters.clientIds.length)
    out.push(plural(filters.clientIds.length, "client", "clients"));
  if (filters.workTypeIds.length)
    out.push(plural(filters.workTypeIds.length, "work type", "work types"));
  return [...out, ...extraLabels].filter(Boolean).slice(0, 10);
};

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

/** True when the person changed something since the view was saved. */
export const viewChanged = (
  saved: Pick<StandardView, "filters" | "extras">,
  current: Pick<StandardView, "filters" | "extras">,
) =>
  saved.filters.preset !== current.filters.preset ||
  !sameIds(saved.filters.userIds, current.filters.userIds) ||
  !sameIds(saved.filters.clientIds, current.filters.clientIds) ||
  !sameIds(saved.filters.workTypeIds, current.filters.workTypeIds) ||
  Object.keys({ ...saved.extras, ...current.extras }).some(
    (k) => (saved.extras[k] ?? "") !== (current.extras[k] ?? ""),
  );

/** What changed, in words ("the date", "the team members"…), for the note on a saved report. */
export const changedParts = (
  saved: Pick<StandardView, "filters" | "extras">,
  current: Pick<StandardView, "filters" | "extras">,
): string[] => {
  const parts: string[] = [];
  if (saved.filters.preset !== current.filters.preset) parts.push("the date");
  if (!sameIds(saved.filters.userIds, current.filters.userIds))
    parts.push("the team members");
  if (!sameIds(saved.filters.clientIds, current.filters.clientIds))
    parts.push("the clients");
  if (!sameIds(saved.filters.workTypeIds, current.filters.workTypeIds))
    parts.push("the work types");
  if (
    Object.keys({ ...saved.extras, ...current.extras }).some(
      (k) => (saved.extras[k] ?? "") !== (current.extras[k] ?? ""),
    )
  )
    parts.push("the view");
  return parts;
};
